// frontend/src/pages/FinancialsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { TrashIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
// --- NOVAS IMPORTAÇÕES ---
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { startOfMonth, endOfMonth, subMonths, formatISO } from 'date-fns';
// ------------------------

// Função auxiliar para formatar moeda (USD) - Mantida do seu código
const formatCurrency = (value) => {
    const number = parseFloat(value);
    if (isNaN(number) || value === null || value === undefined) { // Adicionada verificação para null/undefined
        return '-'; // Ou '$0.00' se preferir
    }
    return number.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// --- Componente Principal ---
const FinancialsPage = () => {
    // --- Estados Existentes (Sumário Geral) ---
    const [summary, setSummary] = useState({
        overallTotalProductCost: '0.00', // Chaves alinhadas com a API
        overallTotalManualPaid: '0.00',
        overallBalance: '0.00',
    });
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summaryError, setSummaryError] = useState('');

    // --- Estados Existentes (Pagamentos Manuais) ---
    const [manualPayments, setManualPayments] = useState([]);
    const [paymentsLoading, setPaymentsLoading] = useState(true);
    const [paymentsError, setPaymentsError] = useState('');
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [isAddingPayment, setIsAddingPayment] = useState(false);
    const [addPaymentError, setAddPaymentError] = useState('');
    const [addPaymentSuccess, setAddPaymentSuccess] = useState('');

    // --- NOVOS ESTADOS PARA SUMÁRIO POR PERÍODO ---
    const [periodSummary, setPeriodSummary] = useState(null);
    const [isLoadingPeriod, setIsLoadingPeriod] = useState(false);
    const [errorPeriod, setErrorPeriod] = useState('');
    const [selectedPeriodLabel, setSelectedPeriodLabel] = useState('Geral');
    const [customStartDate, setCustomStartDate] = useState(null);
    const [customEndDate, setCustomEndDate] = useState(null);
    // ----------------------------------------------

    // Função para buscar o sumário GERAL (ajustada para novas chaves se necessário)
    const fetchOverallSummary = useCallback(async () => {
        setSummaryLoading(true);
        setSummaryError('');
        try {
            const response = await api.get('/financials/summary'); // Sem datas
            setSummary({ // Garante que o estado é preenchido corretamente
                overallTotalProductCost: response.data.overallTotalProductCost || '0.00',
                overallTotalAliexpressPaid: response.data.overallTotalAliexpressPaid || '0.00',
                overallTotalManualPaid: response.data.overallTotalManualPaid || '0.00',
                overallBalance: response.data.overallBalance || '0.00',
            });
        } catch (err) {
            console.error("Erro ao buscar sumário geral:", err);
            setSummaryError(err.response?.data?.message || 'Falha ao carregar sumário geral.');
            setSummary({ overallTotalProductCost: '0.00', overallTotalAliexpressPaid: '0.00', overallTotalManualPaid: '0.00', overallBalance: '0.00' });
        } finally {
            setSummaryLoading(false);
        }
    }, []);

    // Função para buscar pagamentos manuais (como no seu código original)
    const fetchManualPayments = useCallback(async () => {
        setPaymentsLoading(true);
        setPaymentsError('');
        try {
            const response = await api.get('/financials/manual-payments');
            setManualPayments(response.data.items || []);
        } catch (err) {
            console.error("Erro ao buscar pagamentos manuais:", err);
            setPaymentsError(err.response?.data?.message || 'Falha ao carregar pagamentos.');
        } finally {
            setPaymentsLoading(false);
        }
    }, []);

    // --- NOVA FUNÇÃO PARA BUSCAR SUMÁRIO POR PERÍODO ---
    const fetchPeriodSummaryData = useCallback(async (startDate, endDate) => {
        if (!startDate || !endDate) {
            setPeriodSummary(null);
            return;
        }
        setIsLoadingPeriod(true);
        setErrorPeriod('');
        // Atualiza o label aqui também para o período personalizado
        // setSelectedPeriodLabel(`Período: ${startDate.toLocaleDateString('pt-BR')} - ${endDate.toLocaleDateString('pt-BR')}`);

        try {
            const params = new URLSearchParams({
                startDate: formatISO(startDate, { representation: 'date' }),
                endDate: formatISO(endDate, { representation: 'date' }),
            });
            const response = await api.get(`/financials/summary?${params.toString()}`);
            setPeriodSummary(response.data.periodSummary || null);
        } catch (err) {
            console.error("Erro ao buscar sumário do período:", err);
            setErrorPeriod(err.response?.data?.message || 'Falha ao carregar sumário do período.');
            setPeriodSummary(null);
        } finally {
            setIsLoadingPeriod(false);
        }
    }, []);
    // ---------------------------------------------------

    // Efeito para buscar dados ao montar (como no seu código original)
    useEffect(() => {
        fetchOverallSummary();
        fetchManualPayments();
    }, [fetchOverallSummary, fetchManualPayments]);

    // --- Handlers para os botões de período ---
    const handleSetPeriod = (periodType) => {
        let start, end;
        const today = new Date();
        setPeriodSummary(null); // Limpa sumário anterior
        setErrorPeriod(''); // Limpa erro do período

        if (periodType === 'thisMonth') {
            start = startOfMonth(today);
            end = endOfMonth(today);
            setSelectedPeriodLabel('Este Mês');
        } else if (periodType === 'lastMonth') {
            const firstDayLastMonth = startOfMonth(subMonths(today, 1));
            start = firstDayLastMonth;
            end = endOfMonth(firstDayLastMonth);
            setSelectedPeriodLabel('Mês Passado');
        } else if (periodType === 'allTime') {
             setCustomStartDate(null);
             setCustomEndDate(null);
             setSelectedPeriodLabel('Geral');
             return; // Não busca sumário de período, mostra apenas o geral
        }

        if (start && end) {
            setCustomStartDate(start);
            setCustomEndDate(end);
            fetchPeriodSummaryData(start, end);
        }
    };

    const handleFetchCustomPeriod = () => {
        setErrorPeriod('');
        if (customStartDate && customEndDate) {
            if (customStartDate > customEndDate) {
                setErrorPeriod("Data inicial não pode ser maior que a data final.");
                return;
            }
            const labelStart = customStartDate.toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit', year: 'numeric'});
            const labelEnd = customEndDate.toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit', year: 'numeric'});
            setSelectedPeriodLabel(`Personalizado: ${labelStart} - ${labelEnd}`);
            fetchPeriodSummaryData(customStartDate, customEndDate);
        } else {
            setErrorPeriod("Por favor, selecione data de início e fim.");
        }
    };
    // --------------------------------------

    // Funções de CRUD para Pagamentos Manuais (handleAddPayment, handleDeletePayment - do seu código original)
    const handleAddPayment = async (e) => {
        e.preventDefault();
        setIsAddingPayment(true); setAddPaymentError(''); setAddPaymentSuccess('');
        if (!description.trim() || !amount.trim() || !paymentDate.trim()) {
             setAddPaymentError('Todos os campos são obrigatórios.'); setIsAddingPayment(false); return;
        }
        const parsedAmount = parseFloat(amount);
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
            setAddPaymentError('Valor inválido.'); setIsAddingPayment(false); return;
        }
         if (!/^\d{4}-\d{2}-\d{2}$/.test(paymentDate)) {
            setAddPaymentError('Formato da data inválido (YYYY-MM-DD).'); setIsAddingPayment(false); return;
         }
        try {
            await api.post('/financials/manual-payments', { description: description.trim(), amount: parsedAmount, paymentDate });
            setDescription(''); setAmount(''); setPaymentDate(''); setAddPaymentSuccess('Pagamento adicionado!');
            fetchManualPayments(); fetchOverallSummary(); // Recarrega tudo
            setTimeout(() => setAddPaymentSuccess(''), 3000);
        } catch (err) {
            setAddPaymentError(err.response?.data?.message || 'Falha ao adicionar.'); setAddPaymentSuccess('');
        } finally {
            setIsAddingPayment(false);
        }
    };

    const handleDeletePayment = async (paymentId) => {
        if (!window.confirm(`Tem certeza que deseja deletar o pagamento ID ${paymentId}?`)) return;
        setManualPayments(prev => prev.map(p => p.id === paymentId ? { ...p, isDeleting: true } : p));
        try {
            await api.delete(`/financials/manual-payments/${paymentId}`);
            fetchManualPayments(); fetchOverallSummary(); // Recarrega tudo
        } catch (err) {
            alert(`Falha ao deletar: ${err.response?.data?.message || 'Erro'}`);
            setManualPayments(prev => prev.map(p => p.id === paymentId ? { ...p, isDeleting: false } : p));
        }
    };

    // --- JSX ---
    return (
        <div className="p-4 md:p-6 lg:p-8 space-y-8">
            <h1 className="text-2xl font-semibold text-gray-800">Controle Financeiro</h1>

            {/* Seção Sumário Geral (Mantém sua estrutura, acessando 'summary' corretamente) */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-lg font-semibold text-gray-700 mb-4">Sumário Geral</h2>
                {summaryLoading ? ( <p>Carregando sumário...</p> )
                 : summaryError ? ( <p className="text-red-600">{summaryError}</p> )
                 : summary ? (
                   <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-center">
                        <div><p className="text-sm font-medium text-gray-500">Custo Total (Produtos)</p><p className="text-xl font-semibold text-gray-900">{formatCurrency(summary.overallTotalProductCost)}</p><p className='text-xs text-gray-400'>(Baseado nas Variantes)</p></div>
                        <div><p className="text-sm font-medium text-gray-500">Total Pago (Manual)</p><p className="text-xl font-semibold text-gray-900">{formatCurrency(summary.overallTotalManualPaid)}</p><p className='text-xs text-gray-400'>(Pagamentos XTransfer, etc.)</p></div>
                       <div className={`p-4 border rounded-lg ${parseFloat(summary.overallBalance) < 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                            <p className="text-sm font-medium text-gray-500">Saldo</p>
                            <p className={`text-xl font-semibold ${parseFloat(summary.overallBalance) < 0 ? 'text-red-700' : 'text-green-700'}`}>{formatCurrency(summary.overallBalance)}</p>
                            <p className='text-xs text-gray-400'>(Custo - Pagamentos)</p>
                        </div>
                    </div>
                ) : <p>Não foi possível carregar o sumário geral.</p>}
            </div>

            {/* --- NOVA SEÇÃO: Sumário por Período --- */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-lg font-semibold text-gray-700 mb-4">
                    Sumário por Período: <span className="text-indigo-600 font-normal">{selectedPeriodLabel}</span>
                </h2>
                <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                    <div className="flex flex-wrap gap-2">
                        {/* <button onClick={() => handleSetPeriod('allTime')} className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-md shadow-sm">Geral</button> */}
                        <button onClick={() => handleSetPeriod('thisMonth')} className="px-4 py-2 text-sm bg-blue-500 text-white hover:bg-blue-600 rounded-md shadow-sm">Este Mês</button>
                        <button onClick={() => handleSetPeriod('lastMonth')} className="px-4 py-2 text-sm bg-blue-500 text-white hover:bg-blue-600 rounded-md shadow-sm">Mês Passado</button>
                    </div>
                    <div className="flex flex-wrap items-end gap-2 mt-4 sm:mt-0">
                        <div>
                            <label htmlFor="customStartDate" className="block text-xs font-medium text-gray-500 mb-1">De:</label>
                            <DatePicker selected={customStartDate} onChange={(date) => setCustomStartDate(date)} selectsStart startDate={customStartDate} endDate={customEndDate} dateFormat="dd/MM/yyyy" className="w-full sm:w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500" placeholderText="Início" maxDate={new Date()} />
                        </div>
                        <div>
                            <label htmlFor="customEndDate" className="block text-xs font-medium text-gray-500 mb-1">Até:</label>
                            <DatePicker selected={customEndDate} onChange={(date) => setCustomEndDate(date)} selectsEnd startDate={customStartDate} endDate={customEndDate} minDate={customStartDate} dateFormat="dd/MM/yyyy" className="w-full sm:w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500" placeholderText="Fim" maxDate={new Date()} />
                        </div>
                        <button onClick={handleFetchCustomPeriod} className="px-4 py-2 text-sm bg-green-500 text-white hover:bg-green-600 rounded-md shadow-sm self-end" disabled={!customStartDate || !customEndDate || isLoadingPeriod}>
                            {isLoadingPeriod ? 'Buscando...' : 'Aplicar Período'}
                        </button>
                    </div>
                </div>

                {isLoadingPeriod && <p className="text-center text-gray-500 py-4">Carregando sumário do período...</p>}
                {errorPeriod && <p className="text-center text-red-600 py-4">{errorPeriod}</p>}
                {periodSummary && !isLoadingPeriod && !errorPeriod && (
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center pt-4 border-t border-gray-200">
                        <div className="p-4 border rounded-lg">
                            <p className="text-sm font-medium text-gray-500">Custo Total (Produtos)</p>
                            <p className="text-xl font-semibold text-gray-900">{formatCurrency(periodSummary.periodProductCost)}</p>
                           <p className='text-xs text-gray-400'>(Pedidos enviados)</p>
                        </div>
                         <div className="p-4 border rounded-lg">
                             <p className="text-sm font-medium text-gray-500">Total Pago (Manual)</p>
                             <p className="text-xl font-semibold text-gray-900">{formatCurrency(periodSummary.periodManualPaid)}</p>
                         </div>
                    </div>
                )}
                {!periodSummary && !isLoadingPeriod && !errorPeriod && selectedPeriodLabel !== 'Geral' && (
                    <p className="text-sm text-gray-500 italic text-center mt-4 py-4">Selecione um período e clique em "Aplicar Período" ou escolha um filtro rápido.</p>
                )}
            </div>
            {/* --- Fim da Seção de Sumário por Período --- */}


            {/* --- Seção Pagamentos Manuais (Seu código original) --- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Formulário Adicionar Pagamento */}
                <div className="lg:col-span-1">
                    <div className="bg-white p-6 rounded-lg shadow-md h-full">
                        <h2 className="text-lg font-semibold text-gray-700 mb-4">Adicionar Pagamento Manual</h2>
                        <form onSubmit={handleAddPayment} className="space-y-4">
                            <div><label htmlFor="description" className="block text-sm font-medium text-gray-700">Descrição</label><input type="text" id="description" value={description} onChange={(e) => setDescription(e.target.value)} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Ex: Taxa Shopify Mês"/></div>
                            <div><label htmlFor="amount" className="block text-sm font-medium text-gray-700">Valor (USD)</label><input type="number" step="0.01" id="amount" value={amount} onChange={(e) => setAmount(e.target.value)} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Ex: 29.00"/></div>
                            <div><label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700">Data Pagamento</label><input type="date" id="paymentDate" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"/></div>
                            {addPaymentError && <p className="text-sm text-red-600">{addPaymentError}</p>}
                            {addPaymentSuccess && <p className="text-sm text-green-600">{addPaymentSuccess}</p>}
                            <button type="submit" disabled={isAddingPayment} className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                                 <PlusCircleIcon className={`h-5 w-5 mr-2 ${isAddingPayment ? 'animate-spin' : ''}`} />{isAddingPayment ? 'Adicionando...' : 'Adicionar Pagamento'}
                             </button>
                        </form>
                    </div>
                </div>
                {/* Tabela Pagamentos Manuais */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded shadow-md overflow-x-auto">
                         <h2 className="text-lg font-semibold text-gray-700 p-6 pb-0">Histórico de Pagamentos</h2>
                        {paymentsLoading ? ( <p className="p-6 text-center">Carregando histórico...</p> )
                         : paymentsError ? ( <p className="p-6 text-red-600 text-center">{paymentsError}</p> )
                         : manualPayments.length === 0 ? ( <p className="p-6 text-center text-gray-500">Nenhum pagamento manual.</p> )
                         : ( /* ... Sua tabela de pagamentos manuais aqui ... */
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Valor (USD)</th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                                    </tr>
                                </thead>
                                 <tbody className="bg-white divide-y divide-gray-200">
                                    {manualPayments.map((payment) => (
                                         <tr key={payment.id} className={payment.isDeleting ? 'opacity-50' : ''}>
                                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                 {payment.paymentDate ? new Date(payment.paymentDate + 'T00:00:00').toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit', year: 'numeric'}) : '-'}
                                             </td>
                                             <td className="px-6 py-4 text-sm text-gray-900">{payment.description}</td>
                                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(payment.amount)}</td>
                                             <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                 <button onClick={() => handleDeletePayment(payment.id)} disabled={payment.isDeleting} className="text-red-600 hover:text-red-800 disabled:opacity-50" title="Deletar Pagamento">
                                                    <TrashIcon className="h-5 w-5"/>
                                                 </button>
                                             </td>
                                         </tr>
                                    ))}
                                </tbody>
                            </table>
                         )}
                     </div>
                 </div>
            </div>
             {/* --- Fim da Seção Pagamentos Manuais --- */}
        </div>
    );
};

export default FinancialsPage;